import React, { useEffect } from "react"
import { z } from "zod"

import { USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"

interface CreateTagGroupDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		createdTagGroup?: { id: string; name: string; color: string },
	) => void
}

const CreateTagGroupFormSchema = z.object({
	name: z.string().min(1),
	color: z.string().min(1),
})

export const CreateTagGroupDialog = React.memo(
	({ isOpen, onOpenChange }: CreateTagGroupDialogProps) => {
		const form = useZodForm({
			schema: CreateTagGroupFormSchema,
			defaultValues: {
				color: USER_COLOR_PALETTE.Teal.hex,
			},
		})
		const { session } = useSession()
		const zero = useZero()
		const colorWatch = form.watch("color")
		useEffect(() => {
			if (!isOpen) {
				form.reset()
			}
		}, [form, isOpen])
		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(newOpen) => {
					onOpenChange(newOpen)
				}}
				modal={false}
				title="Add group"
				footer={
					<Button form="add-tag-group-form" type="submit">
						Submit
					</Button>
				}
			>
				<Form
					id="add-tag-group-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						if (!session) {
							return
						}
						const id = generateId()
						await mutateOk(
							zero.mutate(
								mutators.tagGroups.create({
									id,
									color: values.color,
									name: values.name,
								}),
							),
						)
						onOpenChange(false, {
							id,
							name: values.name,
							color: values.color,
						})
					}}
				>
					<div className="flex items-end gap-4 px-0.5">
						<FormInput name="name" label="Name" className="flex-1" />
						<ColorPicker
							value={colorWatch}
							onChange={(color) => {
								form.setValue("color", color)
							}}
							withInputs={false}
						/>
					</div>
				</Form>
			</Dialog>
		)
	},
)
